// SPDX-License-Identifier: MIT
/**
 * Rhythm helpers. Everything is expressed in beats of the current scene (env.localBeat), so motion
 * stays locked to the soundtrack whatever the BPM.
 */
import { type Ease, linear } from '../core/ease.ts';
import { clamp } from '../core/math.ts';
import type { SceneEnv } from '../scene.ts';
/** 0→1 progress across local beats [b0, b1], shaped by `ease`. */
export function seg(env: Pick<SceneEnv, 'localBeat'>, b0: number, b1: number, ease: Ease = linear): number {
    return ease(clamp((env.localBeat - b0) / (b1 - b0)));
}
/** Like `seg`, but returns 1 → 0 (for exits). */
export function out(env: Pick<SceneEnv, 'localBeat'>, b0: number, b1: number, ease: Ease = linear): number {
    return 1 - ease(clamp((env.localBeat - b0) / (b1 - b0)));
}
/** Envelope that jumps to 1 on every `every` beats (from `offset`) and decays exponentially. */
export function pulse(env: Pick<SceneEnv, 'localBeat'>, { every = 1, offset = 0, decay = 6 }: {
    every?: number;
    offset?: number;
    decay?: number;
} = {}): number {
    const b = env.localBeat - offset;
    if (b < 0)
        return 0;
    return Math.exp(-((b % every) / every) * decay * every);
}
/** Envelope for explicit hit beats (local), e.g. hits(env, [1, 2.5, 3]). */
export function hits(env: Pick<SceneEnv, 'localBeat'>, beats: number[], decay = 6): number {
    let v = 0;
    for (const h of beats) {
        const d = env.localBeat - h;
        if (d >= 0)
            v = Math.max(v, Math.exp(-d * decay));
    }
    return v;
}
/** Start offset (beats) for item i of n, spreading starts over `spread` beats. */
export function stagger(i: number, n: number, spread: number): number {
    return n <= 1 ? 0 : (i / (n - 1)) * spread;
}
/** Current bar (4 beats) and phase within it. */
export function bar(env: Pick<SceneEnv, 'localBeat'>, beatsPerBar = 4): {
    index: number;
    phase: number;
} {
    const index = Math.floor(env.localBeat / beatsPerBar);
    return { index, phase: (env.localBeat - index * beatsPerBar) / beatsPerBar };
}
/** True between local beats [b0, b1). Handy for gating layers. */
export function during(env: Pick<SceneEnv, 'localBeat'>, b0: number, b1: number): boolean {
    return env.localBeat >= b0 && env.localBeat < b1;
}
